import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO, isSameDay } from "date-fns";
import {
  Calendar as CalendarIcon,
  Users,
  Check,
  FileText,
  Mail,
  Loader2,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "bbc_admin_token";
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` } });

const StatusPill = ({ status }) => {
  const styles =
    status === "paid"
      ? "bg-green-500/20 text-green-400"
      : status === "expired" || status === "failed"
      ? "bg-red-500/20 text-red-400"
      : "bg-yellow-500/20 text-yellow-400";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs uppercase tracking-wider ${styles}`}>
      {status === "paid" && <Check className="w-3 h-3" />}
      {status}
    </span>
  );
};

const KeyValue = ({ label, value, mono = false }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</p>
    <p className={`text-slate-200 text-sm ${mono ? "font-mono" : ""}`}>{value || <span className="text-slate-600">—</span>}</p>
  </div>
);

const BookingDetail = ({ detail, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="booking-detail-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#D4AF37]" />
      </div>
    );
  }
  if (!detail) return null;

  const { booking, intake_form, quiz_result, guide_request, portal_user, latest_report } = detail;
  const attribution = booking?.attribution || {};

  return (
    <div className="p-6 bg-slate-950 border-t-2 border-[#D4AF37]/40 space-y-8" data-testid={`booking-detail-${booking.id}`}>
      <div>
        <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.24em] mb-4">Booking</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <KeyValue label="Client" value={booking.client_name} />
          <KeyValue label="Email" value={booking.client_email} />
          <KeyValue label="Booking ID" value={booking.id} mono />
          <KeyValue label="Payment session" value={booking.payment_session_id || "—"} mono />
          <KeyValue label="Session type" value={booking.session_type} />
          <KeyValue label="Price" value={`€${booking.price_eur}`} />
          <KeyValue label="Reminder sent" value={booking.reminder_sent_at ? format(parseISO(booking.reminder_sent_at), "d MMM HH:mm") : "not yet"} />
          <KeyValue label="No-show recovery" value={booking.no_show_email_sent_at ? "sent" : (booking.status === "completed" ? "n/a — completed" : "not sent")} />
          {booking.status === "cancelled" && booking.cancellation_reason && (
            <KeyValue label="Cancellation reason" value={booking.cancellation_reason.replace(/_/g, ' ')} />
          )}
          {booking.fee_waived_at && (
            <KeyValue label="Fee waiver note" value={booking.fee_waived_note || "no note"} />
          )}
        </div>
      </div>

      {Object.keys(attribution).length > 0 && (
        <div>
          <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.24em] mb-4">Attribution</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {["utm_source", "utm_medium", "utm_campaign", "gclid", "variant"].map((k) => (
              <KeyValue key={k} label={k} value={attribution[k]} />
            ))}
          </div>
        </div>
      )}

      {guide_request && (
        <div>
          <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.24em] mb-4">Guide download</p>
          <p className="text-slate-300 text-sm">
            Downloaded the &ldquo;5 Warning Signs&rdquo; guide on {format(parseISO(guide_request.created_at), "d MMM yyyy")}.
          </p>
        </div>
      )}

      {quiz_result && (
        <div>
          <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.24em] mb-4">Warning-signs quiz</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <KeyValue label="Narcissism" value={`${quiz_result.trait_scores?.narcissism ?? "—"} / 5`} />
            <KeyValue label="Machiavellianism" value={`${quiz_result.trait_scores?.machiavellianism ?? "—"} / 5`} />
            <KeyValue label="Psychopathy" value={`${quiz_result.trait_scores?.psychopathy ?? "—"} / 5`} />
            <KeyValue label="Dominant trait" value={quiz_result.dominant_trait} />
            <KeyValue label="Taken" value={format(parseISO(quiz_result.created_at), "d MMM yyyy")} />
          </div>
        </div>
      )}

      {intake_form ? (
        <div>
          <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.24em] mb-4">Pre-session intake form</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-5">
            <KeyValue label="Submitted" value={intake_form.created_at ? format(parseISO(intake_form.created_at), "d MMM HH:mm") : "—"} />
            {intake_form.leader_gender != null && (
              <KeyValue label="Leader gender" value={intake_form.leader_gender === 0 ? "Male" : "Female"} />
            )}
            {intake_form.years_with_leader != null && (
              <KeyValue label="Years with leader" value={intake_form.years_with_leader} />
            )}
          </div>
          {intake_form.leader_scores && (
            <>
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-3">Leader dark triad</p>
              <div className="grid grid-cols-3 gap-4 mb-5">
                {["narcissism", "machiavellianism", "psychopathy"].map((t) => (
                  <KeyValue key={t} label={t} value={intake_form.leader_scores[t]?.toFixed?.(2) ?? intake_form.leader_scores[t]} />
                ))}
              </div>
            </>
          )}
          {intake_form.participant_scores && (
            <>
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-3">Client Big Five</p>
              <div className="grid grid-cols-5 gap-4">
                {["openness", "conscientiousness", "extraversion", "agreeableness", "neuroticism"].map((t) => (
                  <KeyValue key={t} label={t} value={intake_form.participant_scores[t]?.toFixed?.(2) ?? intake_form.participant_scores[t]} />
                ))}
              </div>
            </>
          )}
          {intake_form.description && (
            <div className="mt-5">
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Description of the situation</p>
              <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">{intake_form.description}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="border border-dashed border-slate-800 p-4 flex items-center gap-3 text-slate-500 text-sm">
          <AlertCircle className="w-4 h-4" />
          The client has not yet filled in the pre-session intake form.
        </div>
      )}

      {portal_user ? (
        <div>
          <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.24em] mb-4">Client portal account</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-4">
            <KeyValue label="Tier" value={portal_user.tier} />
            <KeyValue label="Industry" value={portal_user.industry} />
            <KeyValue label="Country" value={portal_user.country} />
            <KeyValue label="Incidents logged" value={portal_user.incidents_count} />
            <KeyValue label="Big Five completed" value={portal_user.big_five_completed ? "Yes" : "No"} />
            <KeyValue label="Signed up" value={portal_user.created_at ? format(parseISO(portal_user.created_at), "d MMM yyyy") : "—"} />
          </div>
          {latest_report && (
            <div className="border border-slate-800 bg-slate-900/40 p-4">
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-3">Latest vulnerability report</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <KeyValue label="Generated" value={format(parseISO(latest_report.created_at), "d MMM yyyy")} />
                <KeyValue label="Vulnerabilities" value={latest_report.vulnerabilities_count} />
                <KeyValue label="Closest cohort match" value={latest_report.closest_match_percent ? `${latest_report.closest_match_percent}%` : "—"} />
                <KeyValue label="Report ID" value={latest_report.report_id} mono />
              </div>
              {latest_report.escalation_reason && (
                <div className="mt-3 pt-3 border-t border-slate-800">
                  <p className="text-red-400 text-xs uppercase tracking-wider mb-1">Escalation risk</p>
                  <p className="text-slate-300 text-sm">{latest_report.escalation_reason}</p>
                </div>
              )}
              {latest_report.dataset_match_summary && (
                <div className="mt-3 pt-3 border-t border-slate-800">
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Cohort match summary</p>
                  <p className="text-slate-300 text-sm">{latest_report.dataset_match_summary}</p>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="border border-dashed border-slate-800 p-4 flex items-center gap-3 text-slate-500 text-sm">
          <AlertCircle className="w-4 h-4" />
          No matching client-portal account for {booking.client_email}.
        </div>
      )}
    </div>
  );
};

const AdminCalendar = ({ bookings, onSelectBooking }) => {
  const [cursor, setCursor] = useState(new Date());
  const bookingsByDay = useMemo(() => {
    const map = new Map();
    bookings.forEach((b) => {
      if (!b.date) return;
      const key = b.date;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(b);
    });
    return map;
  }, [bookings]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startWeekday = (firstOfMonth.getDay() + 6) % 7; // Monday=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const [selectedDay, setSelectedDay] = useState(null);
  const selectedKey = selectedDay ? format(selectedDay, "yyyy-MM-dd") : null;
  const selectedBookings = selectedKey ? bookingsByDay.get(selectedKey) || [] : [];

  return (
    <div className="grid md:grid-cols-5 gap-6" data-testid="admin-calendar">
      <div className="md:col-span-3 border border-slate-800 bg-slate-900/40 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-xl text-white">{format(cursor, "MMMM yyyy")}</h3>
          <div className="flex gap-2">
            <button
              data-testid="calendar-prev"
              onClick={() => setCursor(new Date(year, month - 1, 1))}
              className="text-slate-300 border border-slate-700 hover:border-[#D4AF37] hover:text-[#D4AF37] px-3 py-1.5 text-xs uppercase tracking-widest transition-colors"
            >
              Prev
            </button>
            <button
              data-testid="calendar-today"
              onClick={() => setCursor(new Date())}
              className="text-slate-300 border border-slate-700 hover:border-[#D4AF37] hover:text-[#D4AF37] px-3 py-1.5 text-xs uppercase tracking-widest transition-colors"
            >
              Today
            </button>
            <button
              data-testid="calendar-next"
              onClick={() => setCursor(new Date(year, month + 1, 1))}
              className="text-slate-300 border border-slate-700 hover:border-[#D4AF37] hover:text-[#D4AF37] px-3 py-1.5 text-xs uppercase tracking-widest transition-colors"
            >
              Next
            </button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <p key={d} className="text-[10px] uppercase tracking-widest text-slate-500 text-center py-1">
              {d}
            </p>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, idx) => {
            if (!day) return <div key={`e${idx}`} />;
            const key = format(day, "yyyy-MM-dd");
            const dayBookings = bookingsByDay.get(key) || [];
            const paidCount = dayBookings.filter((b) => b.payment_status === "paid").length;
            const isSelected = selectedDay && isSameDay(selectedDay, day);
            const isToday = isSameDay(day, new Date());
            return (
              <button
                key={key}
                onClick={() => setSelectedDay(day)}
                data-testid={`calendar-day-${key}`}
                className={`p-2 min-h-[70px] border text-left transition-colors ${
                  isSelected
                    ? "border-[#D4AF37] bg-[#D4AF37]/10"
                    : isToday
                    ? "border-[#38BDF8]/60 hover:border-[#D4AF37]"
                    : dayBookings.length
                    ? "border-slate-700 hover:border-[#D4AF37] bg-slate-900/60"
                    : "border-slate-800 hover:border-slate-600"
                }`}
              >
                <p className={`text-sm ${isToday ? "text-[#38BDF8]" : "text-slate-200"}`}>{day.getDate()}</p>
                {dayBookings.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    <p className="text-[10px] text-[#D4AF37] leading-tight">
                      {dayBookings.length} booking{dayBookings.length === 1 ? "" : "s"}
                    </p>
                    {paidCount > 0 && (
                      <p className="text-[9px] text-green-400 uppercase tracking-widest">{paidCount} paid</p>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="md:col-span-2 border border-slate-800 bg-slate-900/40 p-6" data-testid="calendar-day-detail">
        {selectedDay ? (
          <>
            <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">Selected day</p>
            <h3 className="font-serif text-xl text-white mb-5">{format(selectedDay, "EEEE, d MMMM")}</h3>
            {selectedBookings.length === 0 ? (
              <p className="text-slate-500 text-sm">No bookings on this day.</p>
            ) : (
              <div className="space-y-3">
                {selectedBookings
                  .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
                  .map((b) => (
                    <button
                      key={b.id}
                      onClick={() => onSelectBooking(b)}
                      data-testid={`calendar-booking-${b.id}`}
                      className="w-full text-left border border-slate-800 hover:border-[#D4AF37] bg-slate-950/50 p-4 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-mono text-sm">
                          {b.start_time} – {b.end_time}
                        </span>
                        <StatusPill status={b.payment_status} />
                      </div>
                      <p className="text-slate-200 text-sm">{b.client_name}</p>
                      <p className="text-slate-500 text-xs">
                        {b.session_type === "initial" ? "Initial consultation" : "Follow-up"} · €{b.price_eur}
                      </p>
                    </button>
                  ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16 text-slate-500 text-sm">
            <CalendarIcon className="w-10 h-10 text-slate-700 mx-auto mb-3" />
            Pick a day on the calendar to see the bookings scheduled for it.
          </div>
        )}
      </div>
    </div>
  );
};

export const AdminBookingsTab = ({ bookings, onOpenBrief, briefs, briefLoading, openBrief, generateBrief, caseFileLink, caseFileLinks, caseFileLoading, SessionBriefRow, onRefresh }) => {
  const [openDetail, setOpenDetail] = useState(null);
  const [details, setDetails] = useState({});
  const [detailLoading, setDetailLoading] = useState(null);
  const [nsRunning, setNsRunning] = useState(false);

  const refetchDetail = async (bookingId) => {
    try {
      const res = await axios.get(`${API}/admin/bookings/${bookingId}/detail`, authHeaders());
      setDetails((d) => ({ ...d, [bookingId]: res.data }));
    } catch {
      // ignore — parent refresh will fill in
    }
  };

  const openBooking = async (booking) => {
    if (openDetail === booking.id) {
      setOpenDetail(null);
      return;
    }
    setOpenDetail(booking.id);
    if (details[booking.id]) return;
    setDetailLoading(booking.id);
    try {
      const res = await axios.get(`${API}/admin/bookings/${booking.id}/detail`, authHeaders());
      setDetails((d) => ({ ...d, [booking.id]: res.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load booking details");
    } finally {
      setDetailLoading(null);
    }
  };

  const markCompleted = async (booking) => {
    try {
      await axios.post(`${API}/admin/bookings/${booking.id}/mark-completed`, {}, authHeaders());
      toast.success("Marked as completed — no-show recovery skipped");
      await refetchDetail(booking.id);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update booking");
    }
  };

  const markNoShow = async (booking) => {
    if (!window.confirm(`Flag ${booking.client_name} as a no-show? They'll get a "we missed you" email with a free rebook link on the next sweep.`)) return;
    try {
      await axios.post(`${API}/admin/bookings/${booking.id}/mark-no-show`, {}, authHeaders());
      toast.success("Flagged as no-show — recovery email queued");
      await refetchDetail(booking.id);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not flag as no-show");
    }
  };

  const waiveFee = async (booking) => {
    const fee = Number(booking.cancel_fee_eur || 0);
    if (!window.confirm(`Refund the €${fee.toFixed(2)} cancellation fee back to ${booking.client_name}? This calls Stripe.`)) return;
    const note = window.prompt("Optional note — why are you waiving this? (leave blank to skip)") || "";
    try {
      const res = await axios.post(
        `${API}/admin/bookings/${booking.id}/waive-fee`,
        { reason_note: note.trim() || null },
        authHeaders(),
      );
      toast.success(res.data.message || "Fee waived");
      await refetchDetail(booking.id);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not waive the fee");
    }
  };

  const runNoShow = async () => {
    setNsRunning(true);
    try {
      const res = await axios.post(`${API}/admin/no-show/run`, {}, authHeaders());
      const ac = res.data.auto_complete || {};
      const rec = res.data.recovery || {};
      const rr = res.data.review_rescue || {};
      const bits = [
        `${ac.completed || 0} auto-completed`,
        `${rec.sent || 0} recovery`,
        `${rr.sent || 0} review nudge`,
      ];
      toast.success(`Sweep: ${bits.join(", ")}`);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not run the sweep");
    } finally {
      setNsRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-900/50 border border-slate-800 p-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h2 className="font-serif text-xl text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-[#D4AF37]" />
            All Bookings ({bookings.length})
          </h2>
          <button
            onClick={runNoShow}
            disabled={nsRunning}
            data-testid="run-no-show-btn"
            className="inline-flex items-center gap-2 border border-slate-700 text-slate-300 px-4 py-2 text-xs uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-50 transition-colors"
          >
            {nsRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
            Run no-show recovery
          </button>
        </div>

        {bookings.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-slate-700">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No bookings yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="w-8 py-3 px-2"></th>
                  <th className="text-left py-3 px-4 text-slate-400 text-sm uppercase tracking-wider">Date</th>
                  <th className="text-left py-3 px-4 text-slate-400 text-sm uppercase tracking-wider">Time</th>
                  <th className="text-left py-3 px-4 text-slate-400 text-sm uppercase tracking-wider">Client</th>
                  <th className="text-left py-3 px-4 text-slate-400 text-sm uppercase tracking-wider">Session</th>
                  <th className="text-left py-3 px-4 text-slate-400 text-sm uppercase tracking-wider">Payment</th>
                  <th className="text-left py-3 px-4 text-slate-400 text-sm uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => [
                  <tr
                    key={booking.id}
                    className={`border-b border-slate-800 hover:bg-slate-800/50 cursor-pointer ${
                      openDetail === booking.id ? "bg-slate-900/60" : ""
                    }`}
                    onClick={() => openBooking(booking)}
                    data-testid={`booking-row-${booking.id}`}
                  >
                    <td className="py-4 px-2 text-slate-500">
                      {openDetail === booking.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </td>
                    <td className="py-4 px-4 text-white">{booking.date}</td>
                    <td className="py-4 px-4 text-slate-300">
                      {booking.start_time} - {booking.end_time}
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-white">{booking.client_name}</p>
                      <p className="text-slate-500 text-sm">{booking.client_email}</p>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-slate-300 capitalize">{booking.session_type}</span>
                      <span className="text-[#D4AF37] ml-2">€{booking.price_eur}</span>
                    </td>
                    <td className="py-4 px-4">
                      <StatusPill status={booking.payment_status} />
                      {booking.reminder_sent_at && (
                        <p
                          className="text-slate-500 text-[10px] uppercase tracking-[0.15em] mt-2"
                          data-testid={`reminder-sent-${booking.id}`}
                        >
                          Reminder sent
                        </p>
                      )}
                      {booking.no_show_email_sent_at && (
                        <p className="text-orange-400 text-[10px] uppercase tracking-[0.15em] mt-1" data-testid={`no-show-sent-${booking.id}`}>
                          Recovery sent
                        </p>
                      )}
                      {booking.status === "completed" && (
                        <p className="text-green-500 text-[10px] uppercase tracking-[0.15em] mt-1">
                          {booking.completed_automatically ? "Auto-completed" : "Completed"}
                        </p>
                      )}
                      {booking.status === "no_show" && (
                        <p className="text-orange-400 text-[10px] uppercase tracking-[0.15em] mt-1">No-show</p>
                      )}
                      {booking.status === "cancelled" && (
                        <p className="text-red-400 text-[10px] uppercase tracking-[0.15em] mt-1">Cancelled</p>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => onOpenBrief(booking)}
                          data-testid={`brief-toggle-${booking.id}`}
                          className="inline-flex items-center gap-1.5 border border-slate-700 text-slate-300 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                        >
                          <FileText className="w-3 h-3" />
                          {briefs[booking.id] ? (openBrief === booking.id ? "Hide" : "Brief") : "Prep"}
                        </button>
                        {booking.payment_status === "paid" && !["completed", "rebooked", "cancelled", "no_show"].includes(booking.status) && (
                          <>
                            <button
                              onClick={() => markCompleted(booking)}
                              data-testid={`mark-completed-${booking.id}`}
                              className="inline-flex items-center gap-1.5 border border-slate-700 text-slate-300 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] hover:border-green-500 hover:text-green-400 transition-colors"
                            >
                              <Check className="w-3 h-3" />
                              Done
                            </button>
                            <button
                              onClick={() => markNoShow(booking)}
                              data-testid={`mark-no-show-${booking.id}`}
                              className="inline-flex items-center gap-1.5 border border-slate-700 text-slate-300 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] hover:border-orange-500 hover:text-orange-400 transition-colors"
                            >
                              <AlertCircle className="w-3 h-3" />
                              No-show
                            </button>
                          </>
                        )}
                        {booking.status === "cancelled" && Number(booking.cancel_fee_eur || 0) > 0 && !booking.fee_waived_at && (
                          <button
                            onClick={() => waiveFee(booking)}
                            data-testid={`waive-fee-${booking.id}`}
                            className="inline-flex items-center gap-1.5 border border-slate-700 text-slate-300 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                          >
                            Waive €{Number(booking.cancel_fee_eur).toFixed(0)}
                          </button>
                        )}
                        {booking.status === "cancelled" && booking.fee_waived_at && (
                          <span className="inline-flex items-center gap-1.5 text-[#D4AF37] text-[10px] uppercase tracking-[0.15em]" data-testid={`fee-waived-${booking.id}`}>
                            Fee waived
                          </span>
                        )}
                        {booking.status === "no_show" && !booking.no_show_email_sent_at && (
                          <span className="inline-flex items-center gap-1.5 text-orange-400 text-[10px] uppercase tracking-[0.15em]">
                            <AlertCircle className="w-3 h-3" /> No-show flagged
                          </span>
                        )}
                        {booking.case_file_status === "paid" ? (
                          <span className="inline-flex items-center gap-1.5 text-[#D4AF37] text-[10px] uppercase tracking-[0.15em]" data-testid={`case-file-paid-${booking.id}`}>
                            <Sparkles className="w-3 h-3" /> Case file
                          </span>
                        ) : (
                          <button
                            onClick={() => caseFileLink(booking)}
                            disabled={caseFileLoading === booking.id}
                            data-testid={`case-file-btn-${booking.id}`}
                            className="inline-flex items-center gap-1.5 border border-slate-700 text-slate-300 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-50 transition-colors"
                          >
                            {caseFileLoading === booking.id ? "…" : caseFileLinks[booking.id] || booking.case_file_link ? "Copy €175" : "+€175"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>,
                  openDetail === booking.id ? (
                    <tr key={`${booking.id}-detail`}>
                      <td colSpan={7} className="p-0">
                        <BookingDetail detail={details[booking.id]} loading={detailLoading === booking.id} />
                      </td>
                    </tr>
                  ) : null,
                  openBrief === booking.id ? (
                    <SessionBriefRow
                      key={`${booking.id}-brief`}
                      booking={booking}
                      brief={briefs[booking.id]}
                      loading={briefLoading === booking.id}
                      onGenerate={(b) => generateBrief(b, false)}
                      onEmail={(b) => generateBrief(b, true)}
                    />
                  ) : null,
                ])}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-slate-900/50 border border-slate-800 p-6">
        <h2 className="font-serif text-xl text-white mb-6 flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-[#D4AF37]" />
          Bookings calendar
        </h2>
        <AdminCalendar bookings={bookings} onSelectBooking={openBooking} />
      </div>
    </div>
  );
};
